/**
 * Atlas overlap (ROI voxel-count + probability) for the Reporting stage.
 *
 * Ported from `/home/joshua/Documents/OpenADS/src/ads/atlas/utils.py`
 * (`get_category_features`, `cal_vascular_prob`/`cal_lobe_prob`/`cal_aspects_prob`)
 * and `reporting/roi_volumes.py` (`gen_lesion_report`).
 *
 * DELIBERATE SCOPE CUT vs. the real pipeline (documented, not silent):
 *  - Only the 3 atlases that ship with a matching lookup table AND a NIfTI in
 *    this repo's `openads-wasm/source/assets/atlases/JHU_ICBM/` are wired:
 *    vascular (Arterial), lobe, aspects. ASPECTSPC, BPM Type-I and the
 *    ADC-driven Ventricles are now wired too (see `EXTRA_ATLASES` /
 *    `computeVentricleFeatures` at the bottom). BMOS/BMIS/watershed are
 *    still not (native computes BMS but never writes a CSV for it).
 *  - The real pipeline additionally remaps raw per-ROI counts into fixed
 *    16/21-element "visual" QFV vectors (`vec_VascAtlas2visual`,
 *    `vec_LobeAtlas2visual`) that feed a RandomForest affected/normal
 *    classifier per region. That remap + classifier IS now wired — see
 *    `report/interpretation.ts` (28 of ~34 `assets/models/AA_models/*.pkl`,
 *    converted to ONNX, run client-side) — this module still reports the raw
 *    per-ROI name + voxel overlap + probability directly, since that's a
 *    separate, more transparent table `interpretation.ts` builds its
 *    combined feature vectors from, not a replacement for it.
 *
 * NAMING NOTE: the real Python source hardcodes `*_MNI128.nii` filenames.
 * The atlases actually bundled in this repo's cloned OpenADS source are
 * named `*_MNI182.nii.gz` (182x218x182, standard 1mm MNI152 FSL grid) —
 * verified label-for-label consistent with the lookup tables (e.g. Aspects
 * label 21 "brain" = 1,546,268 voxels in both the .nii.gz and the .pkl), so
 * this is just a naming/version difference in the two source trees, not a
 * different atlas. Confirmed with a one-off Python check
 * (nibabel + pandas), not asserted at runtime.
 */
import { readNifti, reorientVolumeToRAS } from "../nifti";
import { voxelCount, type Dims } from "../volume";
import { fetchAsset } from "../assets";

export interface AtlasLookup {
  regions: string[];
  /** Per-region total voxel count in the atlas's own native grid. */
  volumeVoxels: number[];
}

export interface AtlasDef {
  name: string;
  niftiUrl: string;
  lookupUrl: string;
}

export const ATLASES: Record<"vascular" | "lobe" | "aspects", AtlasDef> = {
  vascular: { name: "Vascular territory", niftiUrl: "/atlases/ArterialAtlas_MNI182.nii.gz", lookupUrl: "/atlases/Vas_Lookup_Volume_df_MNI.json" },
  lobe: { name: "Lobe / anatomical region", niftiUrl: "/atlases/LobeAtlas_MNI182.nii.gz", lookupUrl: "/atlases/Lobe_Lookup_Volume_df_MNI.json" },
  aspects: { name: "ASPECTS region", niftiUrl: "/atlases/AspectsAtlas_MNI182.nii.gz", lookupUrl: "/atlases/Aspects_Lookup_Volume_df_MNI.json" },
};

export interface RegionStat {
  region: string;
  overlapVoxels: number;
  regionTotalVoxels: number;
  probability: number; // overlapVoxels / regionTotalVoxels
}

/** `get_category_features`: binary lesion-voxel count per ROI label 1..max(atlas). */
export function categoryFeatures(lesionBinary: Uint8Array, atlasLabels: Float32Array, numRegions: number): number[] {
  const feats = new Array<number>(numRegions).fill(0);
  for (let i = 0; i < atlasLabels.length; i++) {
    if (!lesionBinary[i]) continue;
    const lbl = Math.round(atlasLabels[i]);
    if (lbl >= 1 && lbl <= numRegions) feats[lbl - 1]++;
  }
  return feats;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetchAsset(url);
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  return res.json() as Promise<T>;
}

async function fetchAtlasNifti(url: string): Promise<{ labels: Float32Array; dims: Dims }> {
  const res = await fetchAsset(url);
  if (!res.ok) throw new Error(`${url} -> ${res.status}`);
  const raw = new Uint8Array(await res.arrayBuffer());
  const gunzipped = raw[0] === 0x1f && raw[1] === 0x8b
    ? new Uint8Array(await new Response(new Blob([raw as BlobPart]).stream().pipeThrough(new DecompressionStream("gzip"))).arrayBuffer())
    : raw;
  // MUST reorient: caught via the left/right sanity check in
  // debug-report-node.mjs — ArterialAtlas/AspectsAtlas/LobeAtlas ship with a
  // negative-x affine diagonal (on-disk LAS-ish, not RAS), same class of bug
  // as nifti.ts's `reorientVolumeToRAS` doc comment describes for the
  // ads_edge ground-truth file. Every volume this app compares index-for-
  // index (lesion mask vs atlas labels) must go through this.
  const vol = reorientVolumeToRAS(readNifti(gunzipped));
  return { labels: vol.voxels, dims: vol.dims };
}

/**
 * Computes ROI overlap + probability for one atlas against a lesion mask
 * that is ALREADY on the same MNI182 grid as the bundled atlas (same
 * PRECONDITION as the rest of this stage: registration is not wired yet).
 * `lesionBinary` and the atlas labels must be the same `dims` (throws if not
 * — this is a hard requirement, not a soft warning, because a silent
 * dimension mismatch would produce a plausible-looking but meaningless
 * probability table).
 */
export async function computeAtlasOverlap(
  atlas: AtlasDef,
  lesionBinary: Uint8Array,
  lesionDims: Dims
): Promise<RegionStat[]> {
  const [{ labels, dims }, lookup] = await Promise.all([
    fetchAtlasNifti(atlas.niftiUrl),
    fetchJson<AtlasLookup>(atlas.lookupUrl),
  ]);
  if (dims[0] !== lesionDims[0] || dims[1] !== lesionDims[1] || dims[2] !== lesionDims[2]) {
    throw new Error(
      `${atlas.name}: atlas grid ${dims.join("x")} does not match lesion mask grid ${lesionDims.join("x")}. ` +
        `This stage assumes the lesion mask is already registered to the MNI182 grid; registration is not wired yet.`
    );
  }
  if (voxelCount(dims) !== labels.length) throw new Error(`${atlas.name}: unexpected voxel count`);

  const feats = categoryFeatures(lesionBinary, labels, lookup.regions.length);
  return lookup.regions.map((region, i) => ({
    region,
    overlapVoxels: feats[i],
    regionTotalVoxels: lookup.volumeVoxels[i],
    probability: lookup.volumeVoxels[i] > 0 ? feats[i] / lookup.volumeVoxels[i] : 0,
  }));
}

export async function computeAllAtlasOverlaps(
  lesionBinary: Uint8Array,
  lesionDims: Dims
): Promise<Record<string, RegionStat[]>> {
  const entries = await Promise.all(
    (Object.entries(ATLASES) as [string, AtlasDef][]).map(
      async ([key, def]) => [key, await computeAtlasOverlap(def, lesionBinary, lesionDims)] as const
    )
  );
  return Object.fromEntries(entries);
}

/**
 * Additional atlases the real pipeline's `reporting_qfv_calculation.py`
 * `AtlasConfig` also runs through the same `get_category_features`
 * primitive: `aspectpc` (posterior-circulation ASPECTS, 8 labels) and
 * `bpm_type1` (JHU BPM Type-I V2, 268 labels). Kept in a separate record
 * (not `ATLASES`) so `computeAllAtlasOverlaps()` and everything keyed on
 * `ATLASES`' 3 entries stays unchanged.
 *
 * The lookup JSONs are one-off conversions of the native
 * `Aspects_PCA_Lookup_Volume_df_MNI.pkl` / `JHU_MNI_SS_BPM_TypeI_V2_Lookup_Volume_df_MNI.pkl`
 * (`df.T.reset_index()`, same as `cal_aspectpc_prob`/`cal_bpm_prob`).
 * `regions` holds `names.json`'s `LabelLookupTable` — the real lesionload
 * CSV column names, which differ from the pkl index for ASPECTSPC
 * (`Thalamus_L` vs pkl `ThalamusL`); the pkl index is kept as `lookupKeys`.
 * For BPM the two are identical (verified, 0 of 268 differ), so
 * `qfv.ts`'s BPM visual remap looks regions up by `region` name.
 */
export const EXTRA_ATLASES: Record<"aspectpc" | "bpmType1", AtlasDef> = {
  aspectpc: { name: "Posterior-circulation ASPECTS region", niftiUrl: "/atlases/AspectsPcaAtlas_MNI182.nii.gz", lookupUrl: "/atlases/Aspects_PCA_Lookup_Volume_df_MNI.json" },
  bpmType1: { name: "JHU BPM Type-I region", niftiUrl: "/atlases/BPMTypeIV2Atlas_MNI182.nii.gz", lookupUrl: "/atlases/JHU_MNI_SS_BPM_TypeI_V2_Lookup_Volume_df_MNI.json" },
};

export async function computeExtraAtlasOverlaps(
  lesionBinary: Uint8Array,
  lesionDims: Dims
): Promise<Record<string, RegionStat[]>> {
  const entries = await Promise.all(
    (Object.entries(EXTRA_ATLASES) as [string, AtlasDef][]).map(
      async ([key, def]) => [key, await computeAtlasOverlap(def, lesionBinary, lesionDims)] as const
    )
  );
  return Object.fromEntries(entries);
}

/**
 * Ventricles (`get_FV_prob`'s `Ventricles_LVO`/`Ventricles_LVI`): NOT a
 * lesion overlap. The real pipeline z-normalises the MNI-space ADC inside
 * the brain mask (`mask > 0.5`; mean / population std + 1e-8, float64),
 * thresholds it at `adc_threshold` (0.5490 — what `process_qfv_single`
 * actually passes, not `calculate()`'s 1600 default) and counts, per
 * `VentriclesEnlargedAtlas` label 1..20:
 *  - LVO: voxels with `adc_norm < thr` (labels 1..10 = LVOR1..10 are kept),
 *  - LVI: voxels with `adc_norm > thr` (labels 11..20 = LVIR1..10 are kept).
 * Volumes come from `LVOR_/LVIR_Lookup_Volume_df_MNI.pkl` (converted to
 * `Ventricles_LVOR_LVIR_Lookup_Volume_df_MNI.json`).
 */
export const VENTRICLES_ATLAS: AtlasDef = {
  name: "Enlarged lateral ventricles",
  niftiUrl: "/atlases/VentriclesEnlargedAtlas_MNI182.nii.gz",
  lookupUrl: "/atlases/Ventricles_LVOR_LVIR_Lookup_Volume_df_MNI.json",
};

export const VENTRICLE_ADC_THRESHOLD = 0.549;

export interface VentricleFeatures {
  /** `names.json` Ventricles LabelLookupTable: LVOR1..10, LVIR1..10. */
  regions: string[];
  /** LVO counts for labels 1..10 (`Ventricles_LVO[:10]`). */
  lvoCounts: number[];
  /** LVI counts for labels 11..20 (`Ventricles_LVI[10:20]`). */
  lviCounts: number[];
  lvorVolumeVoxels: number[];
  lvirVolumeVoxels: number[];
}

interface VentricleLookup {
  regions: string[];
  lvorVolumeVoxels: number[];
  lvirVolumeVoxels: number[];
}

export async function computeVentricleFeatures(
  adcMni: ArrayLike<number>,
  brainMask: ArrayLike<number>,
  dims: Dims,
  adcThreshold: number = VENTRICLE_ADC_THRESHOLD,
): Promise<VentricleFeatures> {
  const [{ labels, dims: aDims }, lookup] = await Promise.all([
    fetchAtlasNifti(VENTRICLES_ATLAS.niftiUrl),
    fetchJson<VentricleLookup>(VENTRICLES_ATLAS.lookupUrl),
  ]);
  if (aDims[0] !== dims[0] || aDims[1] !== dims[1] || aDims[2] !== dims[2]) {
    throw new Error(`${VENTRICLES_ATLAS.name}: atlas grid ${aDims.join("x")} does not match ADC grid ${dims.join("x")}.`);
  }
  const n = labels.length;
  if (adcMni.length !== n || brainMask.length !== n) throw new Error(`${VENTRICLES_ATLAS.name}: ADC/mask voxel count mismatch`);

  let count = 0;
  let sum = 0;
  for (let i = 0; i < n; i++) if (brainMask[i] > 0.5) { sum += adcMni[i]; count++; }
  const mean = count > 0 ? sum / count : 0;
  let sq = 0;
  for (let i = 0; i < n; i++) if (brainMask[i] > 0.5) { const d = adcMni[i] - mean; sq += d * d; }
  const std = count > 0 ? Math.sqrt(sq / count) : 0;

  const lvo = new Array<number>(20).fill(0);
  const lvi = new Array<number>(20).fill(0);
  for (let i = 0; i < n; i++) {
    if (!(brainMask[i] > 0.5)) continue;
    const lbl = Math.round(labels[i]);
    if (lbl < 1 || lbl > 20) continue;
    const z = (adcMni[i] - mean) / (std + 1e-8);
    if (z < adcThreshold) lvo[lbl - 1]++;
    else if (z > adcThreshold) lvi[lbl - 1]++;
  }
  return {
    regions: lookup.regions,
    lvoCounts: lvo.slice(0, 10),
    lviCounts: lvi.slice(10, 20),
    lvorVolumeVoxels: lookup.lvorVolumeVoxels,
    lvirVolumeVoxels: lookup.lvirVolumeVoxels,
  };
}
